import { useCallback, useEffect, useRef, useState } from 'react';
import { getCredits, type CreditsBalance } from './billingApi.js';

/**
 * Hook que mantiene el saldo del usuario en sync (compartido por las apps):
 *
 *  1. Fetch inicial al montar (`GET /api/me/credits`).
 *  2. Listener del evento global `orborix:session.cost.updated` (lo dispara
 *     `@orborix/voice-client` por cada turno cobrado durante una conversación)
 *     → descuento optimista local sin esperar el round-trip al servidor.
 *  3. Re-fetch en `document.visibilitychange === 'visible'` para corregir
 *     drift entre lo optimista y la fuente de verdad del backend.
 *  4. Re-fetch ante `orborix:credits.refresh` (lo dispara la app tras una
 *     recarga acreditada, p. ej. `AddCreditsSheet` de speak).
 *
 *  Los nano viajan como string (BigInt-safe). El parser ignora payloads
 *  malformados — el próximo refresh corrige cualquier desincronización.
 */
export interface UseCreditsResult {
  balance: CreditsBalance | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

export function useCredits(): UseCreditsResult {
  const [balance, setBalance] = useState<CreditsBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const next = await getCredits();
      if (!mountedRef.current) return;
      setBalance(next);
      setError(null);
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  // Fetch inicial.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Descuento optimista por WS event. Si llega un event antes del fetch
  // inicial (`balance === null`), lo ignoramos — el fetch eventualmente trae
  // el saldo correcto del server (el server ya cobró ese turno).
  useEffect(() => {
    const handler = (ev: Event): void => {
      const detail = (ev as CustomEvent<{ deltaNano?: string }>).detail;
      if (!detail || typeof detail.deltaNano !== 'string') return;
      try {
        const delta = BigInt(detail.deltaNano);
        setBalance((prev) => {
          if (!prev) return prev;
          const nextNano = BigInt(prev.balanceNano) - delta;
          // El display lee `balanceNano` y lo convierte a orbs (`formatOrbs`);
          // `balanceUsd` se mantiene sólo por consistencia del shape de la API
          // (no se muestra). Number(BigInt) pierde precisión sobre ~$90M, ok hoy.
          return {
            ...prev,
            balanceNano: nextNano.toString(),
            balanceUsd: (Number(nextNano) / 1e8).toFixed(2),
          };
        });
      } catch {
        /* payload malformado: ignorar — el próximo refresh corrige. */
      }
    };
    window.addEventListener('orborix:session.cost.updated', handler);
    return () => window.removeEventListener('orborix:session.cost.updated', handler);
  }, []);

  // Drift correction: re-fetch al volver a foreground.
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  // Recarga in-app: tras acreditar una compra, la app dispara este evento →
  // re-fetch del saldo desde el server (fuente de verdad).
  useEffect(() => {
    const onCreditsRefresh = (): void => {
      void refresh();
    };
    window.addEventListener('orborix:credits.refresh', onCreditsRefresh);
    return () => window.removeEventListener('orborix:credits.refresh', onCreditsRefresh);
  }, [refresh]);

  return { balance, loading, error, refresh };
}
